/**
 * CreateEventModal
 * Modal for creating new events (attendance/appointments)
 */

import React, { useState } from 'react'
import { eventService } from '../../services/calendar-service'
import type { CreateEventRequest } from '../../types/calendar'
import { toast } from 'sonner'
import { NativeSelect } from "@/components/ui/native-select"
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'

import { getErrorMessage } from '../../utils/error.utils'
interface CreateEventModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  defaultDate?: string
  defaultClientId?: string
  defaultCaseId?: string
}

/**
 * CreateEventModal Component
 */
export const CreateEventModal: React.FC<CreateEventModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultDate,
  defaultClientId,
  defaultCaseId
}) => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<CreateEventRequest>({
    title: '',
    description: '',
    eventType: 'COMPROMISSO',
    eventDate: defaultDate || new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    allDay: false,
    priority: 'MEDIUM',
    locationType: 'PRESENCIAL',
    location: '',
    onlineMeetingLink: '',
    clientId: defaultClientId,
    caseId: defaultCaseId,
    tags: []
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        toast.error('Título é obrigatório')
        return
      }

      if (!formData.eventDate) {
        toast.error('Data é obrigatória')
        return
      }

      if (!formData.allDay && !formData.startTime) {
        toast.error('Hora de início é obrigatória para eventos não diários')
        return
      }

      // Create event
      await eventService.create(formData)

      toast.success('Evento criado com sucesso!')
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      console.error('Error creating event:', error)
      toast.error(getErrorMessage(error, 'Erro ao criar evento'))
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Criar Evento</DialogTitle>
          <DialogDescription>Audiência, reunião ou compromisso na agenda do escritório.</DialogDescription>
        </DialogHeader>

        <form id="create-event-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <FormField label="Título *" htmlFor="event-title">
            <Input
              id="event-title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex: Audiência de Conciliação"
              required
            />
          </FormField>

          <FormField label="Descrição" htmlFor="event-description">
            <Textarea
              id="event-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Detalhes do evento..."
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Tipo de Evento *" htmlFor="event-type">
              <NativeSelect
                id="event-type"
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                required
              >
                <option value="AUDIENCIA">Audiência</option>
                <option value="REUNIAO">Reunião</option>
                <option value="COMPROMISSO">Compromisso</option>
                <option value="OUTRO">Outro</option>
              </NativeSelect>
            </FormField>

            <FormField label="Prioridade" htmlFor="event-priority">
              <NativeSelect
                id="event-priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="LOW">Baixa</option>
                <option value="MEDIUM">Média</option>
                <option value="HIGH">Alta</option>
                <option value="URGENT">Urgente</option>
              </NativeSelect>
            </FormField>
          </div>

          <FormField label="Data *" htmlFor="event-date">
            <Input
              id="event-date"
              type="date"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleChange}
              required
            />
          </FormField>

          <div className="flex items-center gap-2">
            <input
              id="event-all-day"
              type="checkbox"
              name="allDay"
              checked={formData.allDay}
              onChange={handleChange}
              className="size-4 rounded-sm border-input accent-primary"
            />
            <Label htmlFor="event-all-day" className="font-normal">Dia inteiro</Label>
          </div>

          {!formData.allDay && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Hora de Início *" htmlFor="event-start-time">
                <Input
                  id="event-start-time"
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required={!formData.allDay}
                />
              </FormField>
              <FormField label="Hora de Término" htmlFor="event-end-time">
                <Input
                  id="event-end-time"
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
              </FormField>
            </div>
          )}

          <FormField label="Tipo de Local" htmlFor="event-location-type">
            <NativeSelect
              id="event-location-type"
              name="locationType"
              value={formData.locationType}
              onChange={handleChange}
            >
              <option value="PRESENCIAL">Presencial</option>
              <option value="VIRTUAL">Virtual</option>
              <option value="HIBRIDO">Híbrido</option>
            </NativeSelect>
          </FormField>

          {(formData.locationType === 'PRESENCIAL' || formData.locationType === 'HIBRIDO') && (
            <FormField label="Local" htmlFor="event-location">
              <Input
                id="event-location"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Fórum Cível - Sala 301"
              />
            </FormField>
          )}

          {(formData.locationType === 'VIRTUAL' || formData.locationType === 'HIBRIDO') && (
            <FormField label="Link da Reunião" htmlFor="event-meeting-link">
              <Input
                id="event-meeting-link"
                type="url"
                name="onlineMeetingLink"
                value={formData.onlineMeetingLink}
                onChange={handleChange}
                placeholder="https://meet.google.com/..."
              />
            </FormField>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-event-form" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Evento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
